import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import type { ReactElement, ReactNode } from "react";
import { cx, usePortalContainer } from "../presentation.tsx";
import { IconButton } from "./Button.tsx";

export interface DialogProps {
  /** Required: the dialog's accessible name. */
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Footer buttons; wrap a button in <DialogClose> to make it close the dialog. */
  actions?: ReactNode;
  /** Element that opens the dialog, e.g. <Button>Open</Button>. */
  trigger?: ReactElement;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * false = a mandatory decision (e.g. acknowledging a restriction): no corner close button and
   * no outside-click dismissal; the actions must close it. Escape still closes it.
   */
  dismissible?: boolean;
  className?: string;
}

export function Dialog({
  title, description, children, actions, trigger, open, defaultOpen, onOpenChange, dismissible = true, className,
}: DialogProps) {
  const container = usePortalContainer();
  return (
    <BaseDialog.Root
      open={open} defaultOpen={defaultOpen} disablePointerDismissal={!dismissible}
      onOpenChange={onOpenChange ? (o) => onOpenChange(o) : undefined}
    >
      {trigger && <BaseDialog.Trigger render={trigger} />}
      <BaseDialog.Portal container={container}>
        <BaseDialog.Backdrop className="pmu-backdrop" />
        <BaseDialog.Popup className={cx("pmu-popup pmu-dialog", className)}>
          <div className="pmu-dialog__header">
            <BaseDialog.Title className="pmu-dialog__title">{title}</BaseDialog.Title>
            {dismissible &&<BaseDialog.Close render={<IconButton icon="close" label="Close" />} />}
          </div>
          {description && <BaseDialog.Description className="pmu-dialog__description">{description}</BaseDialog.Description>}
          {children && <div className="pmu-dialog__body">{children}</div>}
          {actions && <div className="pmu-dialog__actions">{actions}</div>}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

/** Makes its child element close the enclosing Dialog or Sheet: <DialogClose><Button>Cancel</Button></DialogClose>. */
export function DialogClose({ children }: { children: ReactElement }) {
  return <BaseDialog.Close render={children} />;
}
